import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { Button } from "../../../components/button/Button";
import { ModalContext } from "../../../components/Modal";
import { tabsData } from "../../../redux/queryBuilderSlice";
import { exportRequest } from "../../../redux/exportSlice";
import { showNotification } from "../../../redux/notificationSlice";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCsv,
  faFileExcel,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import "../../../components/dropdown/multiSelectDropdown.scss";

const ExportAllPopup = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isExporting, setIsExporting] = useState(false);
  const modalContext = useContext(ModalContext);
  const close = modalContext ? modalContext.close : () => {};

  const currentTabIndex = useSelector(
    (state: any) => state.queryBuilder.currentTabIndex,
  );
  const tabs = useSelector(tabsData) ?? [];
  const [selectedTabIndexes, setSelectedTabIndexes] = useState<number[]>([]);
  const [tabSelectionError, setTabSelectionError] = useState<string | null>(
    null,
  );
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    setSelectedTabIndexes(tabs.map((_: any, index: number) => index));
  }, []);

  const selectedTabs = useMemo(
    () =>
      selectedTabIndexes
        .map((index) => ({ index, tab: tabs[index] }))
        .filter((item) => item.tab),
    [selectedTabIndexes, tabs],
  );

  const allTabsSelected =
    tabs.length > 0 && selectedTabIndexes.length === tabs.length;

  const handleTabSelectionChange = (tabIndex: number) => {
    setSelectedTabIndexes((prevSelected) => {
      const isSelected = prevSelected.includes(tabIndex);
      return isSelected
        ? prevSelected.filter((index) => index !== tabIndex)
        : [...prevSelected, tabIndex].sort((left, right) => left - right);
    });

    if (tabSelectionError) {
      setTabSelectionError(null);
    }
  };

  const handleSelectAllTabs = () => {
    setSelectedTabIndexes(
      allTabsSelected ? [] : tabs.map((_: any, index: number) => index),
    );

    if (tabSelectionError) {
      setTabSelectionError(null);
    }
  };

  const deselectAllTabs = () => {
    setSelectedTabIndexes([]);
  };

  const handleExport = async (format: string) => {
    if (selectedTabs.length === 0) {
      setTabSelectionError("Select at least one tab to export");
      return;
    }

    setIsExporting(true);
    close();

    try {
      const args = selectedTabs.map(({ tab }) => ({
        isMap: tab.queryMode === "Map",
        vizType: tab.queryMode,
        ...(tab.queryMode !== "Grid" && { subType: tab.querySubType }),
        format,
        queryName: tab.queryName,
        noLimit: false,
        tabName: tab.tabName,
        showFlattened: true,
        gridSorts:
          tab.gridSorts?.map((gridSort: any) => ({
            colId: gridSort.colId,
            sort: gridSort.sort,
            levelUniqueName: gridSort.levelUniqueName,
            sortIndex: gridSort.sortIndex,
          })) || [],
        ...(tab.queryMode === "Map" && {
          mapOptions: {
            measureSelections: tab.msrSelections,
            measureStyleCode:
              tab.querySubType == "thematic" ? "TCVI" : "DDEFAULT",
          },
        }),
      }));

      const formData = new FormData();
      formData.append("args", JSON.stringify(args));

      const name = fileName.trim() || "export";
      const fileUrl = await dispatch(
        exportRequest({ fileName: name, format, formData }),
      ).unwrap();

      if (fileUrl) {
        window.location.href = fileUrl;
      }

      dispatch(
        showNotification({
          message: "Export completed successfully",
          duration: 3000,
        }),
      );
    } catch (error) {
      dispatch(
        showNotification({
          message: "Error exporting tabs",
          duration: 3000,
          isError: true,
        }),
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dialog-container dialog-box-min filter-box">
      <LoadingOverlay show={isExporting} />
      <div className="dialog-header"></div>
      <div className="dialog-title">
        <h3>Export All</h3>
      </div>
      <div className="dialog-body">
        <div className="input-group">
          <label htmlFor="export-all-file-name">File Name</label>
          <input
            type="text"
            id="export-all-file-name"
            className="input-field"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
        </div>
        <div className="input-group">
          <label style={{ marginBottom: "8px" }}>Choose Tabs</label>
          <div className="filter-options-section">
            <div className="filter-checkbox-section">
              <label
                className="radio-inline"
                htmlFor="export-all-tabs"
                style={{ alignItems: "center" }}
              >
                <input
                  id="export-all-tabs"
                  type="checkbox"
                  checked={allTabsSelected}
                  onChange={handleSelectAllTabs}
                  style={{ display: "none" }}
                />
                <span
                  className="msd-checkbox"
                  aria-hidden="true"
                  data-state={allTabsSelected ? "checked" : "unchecked"}
                />
                <span className="ellipsis">Select All</span>
              </label>
            </div>
            <div className="filter-button-section">
              <Button
                color="secondary"
                className="btn md"
                variant="plain"
                id="btn-deselect-all-export-tabs"
                onClick={deselectAllTabs}
                disabled={selectedTabIndexes.length === 0}
              >
                Deselect All
              </Button>
            </div>
          </div>
          <div
            style={{
              maxHeight: "180px",
              overflowY: "auto",
              border: "1px solid #d7dbe0",
              borderRadius: "4px",
              padding: "8px 12px",
            }}
          >
            {tabs.map((tab: any, index: number) => {
              const isSelected = selectedTabIndexes.includes(index);
              const isCurrentTab = index === currentTabIndex;

              return (
                <label
                  key={`${tab.tabName}-${index}`}
                  className="radio-inline"
                  htmlFor={`export-tab-${index}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "4px 0",
                    fontWeight: "normal",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      minWidth: 0,
                    }}
                  >
                    <input
                      id={`export-tab-${index}`}
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTabSelectionChange(index)}
                      style={{ display: "none" }}
                    />
                    <span
                      className="msd-checkbox"
                      aria-hidden="true"
                      data-state={isSelected ? "checked" : "unchecked"}
                    />
                    <span
                      className="ellipsis"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {tab.tabName}
                    </span>
                  </span>
                  {isCurrentTab && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        flexShrink: 0,
                      }}
                    >
                      Current
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          {tabSelectionError && (
            <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
              {tabSelectionError}
            </div>
          )}
        </div>
      </div>

      <div className="dialog-footer">
        <div className="apply-cancel">
          <Button
            color="primary"
            className="btn md"
            variant="solid"
            id="btn-export-all-excel"
            onClick={() => handleExport("xlsx")}
            disabled={isExporting || selectedTabIndexes.length === 0}
          >
            <FontAwesomeIcon icon={faFileExcel} />
            Excel
          </Button>
          {selectedTabIndexes.length === 1 && (
            <Button
              color="primary"
              className="btn md"
              variant="solid"
              id="btn-export-all-csv"
              onClick={() => handleExport("csv")}
              disabled={isExporting }
            >
              <FontAwesomeIcon icon={faFileCsv} />
              CSV
            </Button>
          )}
          <Button
            color="primary"
            className="btn md"
            variant="solid"
            id="btn-export-all-pdf"
            onClick={() => handleExport("pdf")}
            disabled={isExporting || selectedTabIndexes.length === 0}
          >
            <FontAwesomeIcon icon={faFilePdf} />
            PDF
          </Button>
          <Button
            color="primary"
            className="btn md"
            variant="outline"
            id="btn-close-export"
            onClick={close}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportAllPopup;
